import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Newspaper, MapPin, HelpCircle } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import api, { fileUrl } from "@/lib/api";

const ART_FALLBACK = "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=1200&q=80";

export default function Conseils() {
  const [content, setContent] = useState([]);
  useEffect(() => { api.get("/content").then((r) => setContent(r.data)); }, []);

  const articles = content.filter((c) => c.type === "article");
  const faqs = content.filter((c) => c.type === "faq");
  const locals = content.filter((c) => c.type === "local");
  const featured = articles[0];
  const restArticles = articles.slice(1);

  return (
    <div className="bg-cream">
      <Seo title="Conseils, guides & FAQ paysagisme" path="/conseils"
        description="Conseils d'experts, guides pratiques, zones d'intervention et réponses aux questions fréquentes sur l'aménagement et l'entretien de vos espaces extérieurs par Acanthe Jardins." />

      {/* Header */}
      <section className="pt-32 md:pt-40 pb-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-3xl">
            <Overline>Ressources & conseils</Overline>
            <h1 className="mt-4 font-display text-5xl lg:text-6xl text-forest leading-[1.02]">Le carnet du paysagiste</h1>
            <p className="mt-5 text-lg text-stone leading-relaxed">Guides pratiques, astuces d'entretien, zones d'intervention et réponses à vos questions — par les experts d'Acanthe Jardins.</p>
          </Reveal>
        </div>
      </section>

      {content.length === 0 && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="rounded-3xl border border-dashed border-border bg-white py-24 text-center text-stone">Nos articles et guides arrivent très bientôt.</div>
        </div>
      )}

      {/* Blog / articles */}
      {articles.length > 0 && (
        <section className="py-14">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-8">
              <Newspaper className="text-terracotta" size={22} />
              <h2 className="font-display text-3xl text-forest">Articles & conseils</h2>
            </div>

            {featured && (
              <Reveal>
                <Link to={`/conseils/${featured.slug}`} data-testid={`content-${featured.slug}`}
                  className="group grid lg:grid-cols-2 rounded-[28px] overflow-hidden bg-white border border-border hover:shadow-xl hover:shadow-forest/5 transition-all mb-8">
                  <div className="relative min-h-[260px] lg:min-h-[380px] overflow-hidden order-first">
                    <img src={featured.cover_image ? fileUrl(featured.cover_image) : ART_FALLBACK} alt={featured.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-terracotta">À la une</span>
                    <h3 className="mt-3 font-display text-3xl lg:text-4xl text-forest leading-tight">{featured.title}</h3>
                    <p className="mt-4 text-stone leading-relaxed line-clamp-3">{featured.excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-semibold text-brand group-hover:gap-3 transition-all">Lire l'article <ArrowRight size={18} /></span>
                  </div>
                </Link>
              </Reveal>
            )}

            {restArticles.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {restArticles.map((c, i) => (
                  <Reveal key={c.id} delay={(i % 3) * 0.07}>
                    <Link to={`/conseils/${c.slug}`} data-testid={`content-${c.slug}`}
                      className="group flex flex-col h-full rounded-[22px] overflow-hidden bg-white border border-border hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest/5 transition-all">
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img src={c.cover_image ? fileUrl(c.cover_image) : ART_FALLBACK} alt={c.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <h3 className="font-display text-xl text-forest leading-tight">{c.title}</h3>
                        <p className="text-sm text-stone mt-2 line-clamp-3 flex-1">{c.excerpt}</p>
                        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand group-hover:gap-2 transition-all">Lire <ArrowRight size={14} /></span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Zones d'intervention (local) */}
      {locals.length > 0 && (
        <section className="py-14 bg-[#F0EFEA]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-8">
              <MapPin className="text-terracotta" size={22} />
              <div>
                <h2 className="font-display text-3xl text-forest">Nos zones d'intervention</h2>
                <p className="text-sm text-stone">Paysagiste près de chez vous</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {locals.map((c, i) => (
                <Reveal key={c.id} delay={(i % 4) * 0.06}>
                  <Link to={`/conseils/${c.slug}`} data-testid={`content-${c.slug}`}
                    className="group flex items-center justify-between gap-3 rounded-2xl bg-white border border-border p-5 hover:border-brand transition-colors">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-terracotta">{c.ville} {c.code_postal}</p>
                      <p className="font-display text-lg text-forest leading-tight truncate">{c.title}</p>
                    </div>
                    <ArrowUpRight size={18} className="text-brand shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <Reveal className="text-center mb-10">
              <div className="mx-auto h-12 w-12 rounded-2xl bg-forest flex items-center justify-center mb-4"><HelpCircle className="text-cream" size={24} /></div>
              <h2 className="font-display text-4xl text-forest">Questions fréquentes</h2>
              <p className="mt-3 text-stone">Tout ce qu'il faut savoir sur nos prestations</p>
            </Reveal>
            <Reveal>
              <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
                {faqs.map((c, i) => (
                  <AccordionItem key={c.id} value={c.id} data-testid={`faq-${i}`}
                    className="rounded-2xl border border-border bg-white px-5 overflow-hidden">
                    <AccordionTrigger className="text-left font-display text-lg text-forest hover:no-underline py-5">{c.title}</AccordionTrigger>
                    <AccordionContent className="text-stone">
                      <p className="leading-relaxed">{c.excerpt}</p>
                      <Link to={`/conseils/${c.slug}`} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:gap-2 transition-all">Voir toutes les réponses <ArrowRight size={14} /></Link>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      )}
    </div>
  );
}
